/**
 * Systemadmin: tenants (med brukere og Zaptec-kontakt), jobbstatus og
 * manuell jobb-kjøring. Sysadmin opererer i valgfri tenant via ?tenant=.
 */
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fmtTid, type Person, type VendorAccount } from '../lib/api.ts';
import { Field, inputKlasse, knappPrimær, knappSekundær, Modal } from '../components/ui.tsx';
import { KontoRedigerModal, KontoStatus } from '../components/konto.tsx';

interface TenantRow {
  id: string;
  navn: string;
  orgNr: string | null;
  aktiv: boolean;
  opprettet: string;
  antPersoner: number;
  antKontoer: number;
  antKontaktOk: number;
}

interface JobRun {
  id: number;
  jobb: string;
  status: string;
  detaljer: string | null;
  startTs: string;
  sluttTs: string;
}

function KontaktCelle({ t }: { t: TenantRow }) {
  if (t.antKontoer === 0) return <span className="text-slate-400">Ingen kontoer</span>;
  if (t.antKontaktOk === t.antKontoer)
    return <span className="font-medium text-green-700">✓ Kontakt OK</span>;
  return (
    <span className="font-medium text-red-600">
      ✗ Ikke kontakt{t.antKontoer > 1 ? ` (${t.antKontaktOk}/${t.antKontoer} OK)` : ''}
    </span>
  );
}

export default function System() {
  const qc = useQueryClient();
  const [nyTenant, setNyTenant] = useState(false);
  const [redigerTenant, setRedigerTenant] = useState<TenantRow | null>(null);

  const tenantsQuery = useQuery<TenantRow[]>({
    queryKey: ['tenants'],
    queryFn: () => api.get('/system/tenants'),
    refetchInterval: 60_000,
  });
  const jobsQuery = useQuery<{ kjente: string[]; kjøringer: JobRun[] }>({
    queryKey: ['jobs'],
    queryFn: () => api.get('/system/jobs'),
    refetchInterval: 30_000,
  });

  const kjørJobb = useMutation({
    mutationFn: (navn: string) => api.post(`/system/jobs/${navn}/run`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  });

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-800">Tenants</h1>
          <button onClick={() => setNyTenant(true)} className={knappPrimær}>
            Ny tenant
          </button>
        </div>
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3">Navn</th>
                <th className="px-4 py-3">Org.nr</th>
                <th className="px-4 py-3">Personer</th>
                <th className="px-4 py-3">Zaptec-kontakt</th>
                <th className="px-4 py-3">Opprettet</th>
                <th className="px-4 py-3">Aktiv</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {(tenantsQuery.data ?? []).map((t) => (
                <tr key={t.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2 font-medium text-slate-700">{t.navn}</td>
                  <td className="px-4 py-2">{t.orgNr ?? '–'}</td>
                  <td className="px-4 py-2">{t.antPersoner}</td>
                  <td className="px-4 py-2">
                    <KontaktCelle t={t} />
                  </td>
                  <td className="px-4 py-2">{fmtTid(t.opprettet)}</td>
                  <td className="px-4 py-2">{t.aktiv ? 'Ja' : 'Nei'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => setRedigerTenant(t)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Rediger
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-lg font-bold text-slate-800">Bakgrunnsjobber</h2>
        <div className="mb-3 flex gap-2">
          {(jobsQuery.data?.kjente ?? []).map((j) => (
            <button
              key={j}
              onClick={() => kjørJobb.mutate(j)}
              disabled={kjørJobb.isPending}
              className={knappSekundær}
            >
              Kjør {j}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="px-4 py-3">Jobb</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Start</th>
                <th className="px-4 py-3">Detaljer</th>
              </tr>
            </thead>
            <tbody>
              {(jobsQuery.data?.kjøringer ?? []).map((k) => (
                <tr key={k.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2 font-medium">{k.jobb}</td>
                  <td className="px-4 py-2">
                    <span className={k.status === 'OK' ? 'text-green-700' : 'text-red-600'}>{k.status}</span>
                  </td>
                  <td className="px-4 py-2">{fmtTid(k.startTs)}</td>
                  <td className="max-w-md truncate px-4 py-2 text-xs text-slate-500" title={k.detaljer ?? ''}>
                    {k.detaljer ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {nyTenant && (
        <TenantModal
          onClose={() => {
            setNyTenant(false);
            qc.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
      {redigerTenant && (
        <RedigerTenantModal
          tenant={redigerTenant}
          onClose={() => {
            setRedigerTenant(null);
            qc.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
    </div>
  );
}

// ---------- Rediger tenant (med brukere og Zaptec-kontoer) ----------

function RedigerTenantModal({ tenant, onClose }: { tenant: TenantRow; onClose: () => void }) {
  const qc = useQueryClient();
  const tq = `tenant=${tenant.id}`;
  const [feil, setFeil] = useState<string | null>(null);
  const [lagret, setLagret] = useState(false);
  const [redigerBruker, setRedigerBruker] = useState<Person | 'ny' | null>(null);
  const [redigerKonto, setRedigerKonto] = useState<VendorAccount | null>(null);

  const personsQuery = useQuery<Person[]>({
    queryKey: ['tenant-persons', tenant.id],
    queryFn: () => api.get(`/persons?${tq}`),
  });
  const kontoerQuery = useQuery<VendorAccount[]>({
    queryKey: ['tenant-kontoer', tenant.id],
    queryFn: () => api.get(`/vendor-accounts?${tq}`),
    refetchInterval: 30_000,
  });

  const synk = useMutation({
    mutationFn: (id: string) => api.post(`/vendor-accounts/${id}/sync?${tq}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-kontoer', tenant.id] });
      setFeil(null);
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Synk feilet'),
  });

  const fjernBruker = useMutation({
    mutationFn: (personId: string) => api.delete(`/persons/${personId}?${tq}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-persons', tenant.id] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      setFeil(null);
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Kunne ikke fjerne bruker'),
  });

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLagret(false);
    const fd = new FormData(e.currentTarget);
    try {
      await api.patch(`/system/tenants/${tenant.id}`, {
        navn: fd.get('navn'),
        orgNr: fd.get('orgNr') || null,
        aktiv: fd.get('aktiv') === 'ja',
      });
      setFeil(null);
      setLagret(true);
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel={`Tenant: ${tenant.navn}`} onClose={onClose} bred>
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Navn">
          <input name="navn" required defaultValue={tenant.navn} className={inputKlasse} />
        </Field>
        <Field label="Org.nr (valgfritt)">
          <input name="orgNr" defaultValue={tenant.orgNr ?? ''} className={inputKlasse} />
        </Field>
        <Field label="Aktiv">
          <select name="aktiv" defaultValue={tenant.aktiv ? 'ja' : 'nei'} className={inputKlasse}>
            <option value="ja">Ja</option>
            <option value="nei">Nei</option>
          </select>
        </Field>
        <div className="sm:col-span-3 flex items-center justify-end gap-3">
          {lagret && <span className="text-sm text-green-700">Lagret</span>}
          <button type="submit" className={knappPrimær}>
            Lagre tenant
          </button>
        </div>
      </form>

      <hr className="my-4 border-slate-200" />

      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Brukere</h3>
        <button onClick={() => setRedigerBruker('ny')} className={knappSekundær}>
          Ny bruker
        </button>
      </div>
      {(personsQuery.data ?? []).length === 0 ? (
        <p className="mb-2 text-sm text-amber-600">
          Tenanten har ingen brukere — opprett en admin så den kan tas i bruk.
        </p>
      ) : (
        <table className="mb-2 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3">Navn</th>
              <th className="py-2 pr-3">E-post (brukernavn)</th>
              <th className="py-2 pr-3">Rolle</th>
              <th className="py-2 pr-3">Aktiv</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(personsQuery.data ?? []).map((p) => (
              <tr key={p.id} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-700">{p.navn}</td>
                <td className="py-2 pr-3">{p.epost}</td>
                <td className="py-2 pr-3">{p.rolle}</td>
                <td className="py-2 pr-3">{p.aktiv ? 'Ja' : 'Nei'}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => setRedigerBruker(p)}
                    className="mr-3 text-xs text-blue-600 hover:underline"
                  >
                    Rediger
                  </button>
                  <button
                    onClick={() => fjernBruker.mutate(p.id)}
                    disabled={fjernBruker.isPending}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Fjern
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <hr className="my-4 border-slate-200" />

      <h3 className="mb-2 text-sm font-semibold text-slate-700">Zaptec-kontoer</h3>
      {(kontoerQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-slate-500">
          Ingen leverandørkontoer. Tenant-admin legger dem inn under Register → Leverandørkontoer.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 pr-3">Navn</th>
              <th className="py-2 pr-3">Brukernavn</th>
              <th className="py-2 pr-3">Kontakt</th>
              <th className="py-2 pr-3">Sist synket</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(kontoerQuery.data ?? []).map((k) => (
              <tr key={k.id} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-medium text-slate-700">{k.navn}</td>
                <td className="py-2 pr-3">{k.username ?? '–'}</td>
                <td className="py-2 pr-3">
                  <KontoStatus status={k.status} feil={k.sisteFeil} />
                </td>
                <td className="py-2 pr-3">{fmtTid(k.sisteSynk)}</td>
                <td className="py-2 text-right">
                  <button
                    onClick={() => synk.mutate(k.id)}
                    disabled={synk.isPending}
                    className="mr-3 text-xs text-blue-600 hover:underline"
                  >
                    Synk nå
                  </button>
                  <button
                    onClick={() => setRedigerKonto(k)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Rediger
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {feil && <p className="mt-3 text-sm text-red-600">{feil}</p>}

      {redigerBruker && (
        <BrukerModal
          person={redigerBruker === 'ny' ? null : redigerBruker}
          tenantQuery={tq}
          onClose={() => {
            setRedigerBruker(null);
            qc.invalidateQueries({ queryKey: ['tenant-persons', tenant.id] });
            qc.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
      {redigerKonto && (
        <KontoRedigerModal
          konto={redigerKonto}
          tenantQuery={tq}
          onClose={() => {
            setRedigerKonto(null);
            qc.invalidateQueries({ queryKey: ['tenant-kontoer', tenant.id] });
            qc.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
    </Modal>
  );
}

function BrukerModal({
  person,
  tenantQuery,
  onClose,
}: {
  person: Person | null;
  tenantQuery: string;
  onClose: () => void;
}) {
  const [feil, setFeil] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      navn: fd.get('navn'),
      epost: fd.get('epost'),
      rolle: fd.get('rolle'),
    };
    const passord = fd.get('passord') as string;
    if (passord) body.passord = passord;
    if (person) body.aktiv = fd.get('aktiv') === 'ja';
    try {
      if (person) await api.patch(`/persons/${person.id}?${tenantQuery}`, body);
      else await api.post(`/persons?${tenantQuery}`, body);
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel={person ? `Rediger ${person.navn}` : 'Ny bruker'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Navn">
          <input name="navn" required defaultValue={person?.navn} className={inputKlasse} />
        </Field>
        <Field label="E-post (brukernavn ved innlogging)">
          <input name="epost" type="email" required defaultValue={person?.epost} className={inputKlasse} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rolle">
            <select name="rolle" defaultValue={person?.rolle ?? 'admin'} className={inputKlasse}>
              <option value="admin">Admin</option>
              <option value="bruker">Bruker</option>
            </select>
          </Field>
          {person && (
            <Field label="Aktiv">
              <select name="aktiv" defaultValue={person.aktiv ? 'ja' : 'nei'} className={inputKlasse}>
                <option value="ja">Ja</option>
                <option value="nei">Nei</option>
              </select>
            </Field>
          )}
        </div>
        <Field label={person ? 'Nytt passord (tomt = uendret)' : 'Passord (min. 8 tegn)'}>
          <input name="passord" type="password" className={inputKlasse} />
        </Field>
        {!person && (
          <p className="text-xs text-slate-400">
            Finnes e-posten fra før, gjenbrukes brukeren (samme innlogging på tvers av tenants) og
            passordfeltet kan stå tomt. For helt nye brukere kreves passord.
          </p>
        )}
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" className={knappPrimær}>
            Lagre
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Ny tenant ----------

function TenantModal({ onClose }: { onClose: () => void }) {
  const [feil, setFeil] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.post('/system/tenants', {
        navn: fd.get('navn'),
        orgNr: fd.get('orgNr') || null,
        adminNavn: fd.get('adminNavn'),
        adminEpost: fd.get('adminEpost'),
        adminPassord: fd.get('adminPassord'),
      });
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel="Ny tenant" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Navn (husholdning/borettslag/firma)">
          <input name="navn" required className={inputKlasse} />
        </Field>
        <Field label="Org.nr (valgfritt)">
          <input name="orgNr" className={inputKlasse} />
        </Field>
        <hr className="border-slate-200" />
        <p className="text-sm font-medium text-slate-600">Første administrator</p>
        <Field label="Navn">
          <input name="adminNavn" required className={inputKlasse} />
        </Field>
        <Field label="E-post">
          <input name="adminEpost" type="email" required className={inputKlasse} />
        </Field>
        <Field label="Passord (min. 8 tegn)">
          <input name="adminPassord" type="password" required minLength={8} className={inputKlasse} />
        </Field>
        <p className="text-xs text-slate-400">
          Finnes e-posten fra før, gjenbrukes brukeren som admin i den nye tenanten — passordet
          beholdes da uendret.
        </p>
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" className={knappPrimær}>
            Opprett
          </button>
        </div>
      </form>
    </Modal>
  );
}
